import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import apiClient from '../api/client'

interface CollectionInfo {
  name: string
  count: number
}

function NewResearchPage(): JSX.Element {
  const navigate = useNavigate()
  const [question, setQuestion] = useState('')
  const [collections, setCollections] = useState<CollectionInfo[]>([])
  const [collection, setCollection] = useState('')
  const [newCollectionName, setNewCollectionName] = useState('')
  const [isNewCollection, setIsNewCollection] = useState(false)
  const [minIterations, setMinIterations] = useState(2)
  const [maxIterations, setMaxIterations] = useState(5)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    apiClient
      .get<CollectionInfo[]>('/research/collections')
      .then((res) => {
        if (cancelled) return
        const data = res.data as unknown as CollectionInfo[]
        setCollections(data)
        if (data.length > 0 && !isNewCollection) {
          setCollection(data[0].name)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const handleCollectionChange = (value: string) => {
    if (value === '__new__') {
      setIsNewCollection(true)
      setCollection('')
    } else {
      setIsNewCollection(false)
      setCollection(value)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedQuestion = question.trim()
    if (trimmedQuestion.length < 10) {
      setError('Research question must be at least 10 characters')
      return
    }
    const collectionName = isNewCollection ? newCollectionName.trim() : collection.trim()
    if (!collectionName) {
      setError('Please select or enter a collection name')
      return
    }
    if (maxIterations < minIterations) {
      setError('Max iterations must be >= min iterations')
      return
    }

    setLoading(true)
    try {
      const qRes = await apiClient.post<{ id: string }>('/research/questions', {
        question_text: trimmedQuestion,
      })
      const questionId = (qRes.data as { id: string }).id

      const runRes = await apiClient.post<{ id: string }>('/research/runs', {
        research_question_id: questionId,
        collection_name: collectionName,
        min_iterations: minIterations,
        max_iterations: maxIterations,
      })
      const runId = (runRes.data as { id: string }).id
      navigate(`/runs/${runId}`)
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string | string[] } }; message?: string }
      let msg = 'Failed to start research'
      if (maybeAxios?.response?.data?.detail) {
        const detail = maybeAxios.response.data.detail
        msg = Array.isArray(detail) ? detail.map((d: unknown) => (typeof d === 'string' ? d : JSON.stringify(d))).join(', ') : detail
      } else if (maybeAxios?.message) {
        msg = maybeAxios.message
      }
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Start New Research</h1>
        <Link to="/dashboard" className="page-action-secondary">
          Back to Dashboard
        </Link>
      </div>
      <form className="research-form" onSubmit={handleSubmit}>
        <label className="form-label">
          Research Question
          <textarea
            className="form-input form-textarea"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            required
            minLength={10}
            placeholder="e.g. What is the impact of transformers on protein folding?"
          />
        </label>

        <label className="form-label">
          Knowledge Base Collection
          <select
            className="form-input"
            value={isNewCollection ? '__new__' : collection}
            onChange={(e) => handleCollectionChange(e.target.value)}
            required
          >
            {collections.length === 0 && <option value="">No existing collections — create new</option>}
            {collections.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.count} docs)
              </option>
            ))}
            <option value="__new__">Create new collection</option>
          </select>
        </label>

        {isNewCollection && (
          <label className="form-label">
            New Collection Name
            <input
              type="text"
              className="form-input"
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              required
              placeholder="e.g. my-chip-design-kb"
              pattern="^[a-zA-Z0-9_-]+$"
              title="Letters, numbers, underscore and hyphen only"
            />
          </label>
        )}

        <div className="form-row">
          <label className="form-label">
            Min Iterations
            <input
              type="number"
              className="form-input"
              min={1}
              max={20}
              value={minIterations}
              onChange={(e) => setMinIterations(Number(e.target.value))}
              required
            />
          </label>
          <label className="form-label">
            Max Iterations
            <input
              type="number"
              className="form-input"
              min={minIterations}
              max={20}
              value={maxIterations}
              onChange={(e) => setMaxIterations(Number(e.target.value))}
              required
            />
          </label>
        </div>

        {error && <p style={{ color: '#c00', fontSize: 14 }}>{error}</p>}

        <button type="submit" className="form-submit" disabled={loading}>
          {loading ? 'Starting...' : 'Start Research'}
        </button>
      </form>
    </div>
  )
}

export default NewResearchPage
